import { Unit } from '../components/ServiceItem';
import { SlotHour } from '../modules/slots';

import { DisplayTimes } from '../pages/DateTime';
import { SchedulesTypes } from '../utils/constants';
import { makeError } from '../utils/error';
import { ProductItemProps } from './Product';
import { Specialty } from './Specialist';
import { createUnitAppointment, UnitAppointment } from './UnitSchedule';
import { PatientProps } from '../contexts/PatientContext';

type availableSchedules = {
  [key: string]: [{ intervalFinish: string; intervalStart: string }];
};

export type EavSlotsProps = {
  patient: string;
  availableSchedules: availableSchedules;
};

export enum PAYMENT {
  PARTICULAR = 1,
  B2B = 2,
  HEALTH_INSURANCE = 3
}

export type Payment = {
  type?: string | number;
  price?: string;
  healthInsurance?: {
    id: string;
    name: string;
    external_id: string | null;
  };
  healthPlan?: {
    id: string;
    name: string;
    external_id: string | null;
  };
  eligibility?: {
    idInsurance: number;
    idCustomer: number;
    codInsurance: string;
    codPlan: string;
    codOffice: string;
    plan: string;
    image: string;
  };
};

export const typeLocationSchedule = {
  PRESENTIAL: 'PRESENTIAL',
  EINSTEIN_TO_YOU: 'EINSTEIN_TO_YOU',
  TELEMEDICINE: 'TELEMEDICINE',
  DRIVE_THRU: 'DRIVE_THRU',
  PROCEDURE: 'PROCEDURE'
};

export function typeFiltered(type: string) {
  return Object.keys(typeLocationSchedule).find(item => item === type);
}

export type Resource = {
  id?: string | number;
  name: string;
  role: string;
  crm: string;
  image?: string;
  roleList?: Specialty[];
};

export type ScheduleItemProps = {
  productId: string;
  name: string;
  price: string;
  discount: string;
  unitId?: string;
  itemId?: string;
  catalogItemId?: string;
  careType?: string;
  payment?: Payment;
  date?: string;
  startHour?: string;
  endHour?: string;
  time?: DisplayTimes;
  idPatient?: string;
  patientName?: string;
  id: string;
  productIdLocal?: string;
  selectedSlotOptions?: SlotHour[];
  isKit?: boolean;
  productName?: string;
  scheduleType?: string;
  resource?: Resource;
  resourceId?: string;
  useResouce?: boolean;
  channel?: string;
  idConvenio?: string;
  unit?: UnitAppointment;
  localId?: string;
  patient?: PatientProps;
};

export function ScheduleItem(schedule: ScheduleItemProps): ScheduleItemProps {
  return schedule;
}

export const ScheduleItemPropsBuilder = (scheduleItem?: ScheduleItemProps) => {
  const schedule = scheduleItem || ({} as ScheduleItemProps);

  const checkPropertyError = () => {
    // if (!schedule.idPatient) makeError('Missing schedule.idPatient property');
    if (!schedule.productId) makeError('Missing (setProductInfo) schedule.productId property');
    // if (!schedule.productIdLocal)
    //   makeError('Missing schedule.productIdLocal property');
    // if (!schedule.name) makeError('Missing schedule.name property');

    // if (!schedule.price) makeError('Missing schedule.price property');
    // if (!schedule.discount) makeError('Missing schedule.discount property');
    if (!schedule.productName) makeError('Missing schedule.productName property');

    if (!schedule.itemId) makeError('Missing (setProductInfo) schedule.itemId property');
    if (!schedule.catalogItemId) makeError('Missing (setProductInfo) schedule.catalogItemId property');

    if (!schedule.careType) makeError('Missing (setScheduleType) schedule.careType property');
    if (!schedule.scheduleType) makeError('Missing schedule.scheduleType property');

    if (!schedule.payment) makeError('Missing (setPayment) schedule.payment property');

    if (!schedule.unit) makeError('Missing (setUnit) schedule.unit property');

    if (!schedule.selectedSlotOptions) makeError('Missing (setSlotsOptions) schedule.selectedSlotOptions property');
    if (!schedule.time) makeError('Missing (setTime) schedule.time property');
    if (!schedule.payment) makeError('Missing (setSlotsOptions) schedule.payment property');
    // if (!schedule.unit)
    //   makeError('Missing (setSlotsOptions) schedule.unit property');
    if (!schedule.selectedSlotOptions) makeError('Missing (setSlotsOptions) schedule.selectedSlotOptions property');
    if (!schedule.catalogItemId) makeError('Missing (schedule) schedule.catalogItemId property');
  };

  function setPatient(patientId: string) {
    schedule.idPatient = patientId;
    return schedule;
  }

  function setPatientFull(patient: PatientProps) {
    schedule.patient = patient;
    return schedule;
  }

  function setPatientName(name: string) {
    schedule.patientName = name;
    return schedule;
  }

  function setProduct(product: ProductItemProps) {
    schedule.productId = product.id;
    schedule.productIdLocal = product.idLocal || '';
    schedule.name = product.name;
    schedule.price = product.price;
    schedule.discount = product.discount;
    schedule.id = 'fixed_id';
    schedule.productName = product.name;
    let scheduleType;
    if (product.coverages)
      scheduleType =
        product?.coverages?.[0]?.category === SchedulesTypes.APPOINTMENT
          ? SchedulesTypes.CONSULTATION
          : product.coverages[0].category;
    schedule.scheduleType = scheduleType;
    return schedule;
  }

  // function setProductInfo(props: {
  //   itemId: string;
  //   productId: string;
  //   productName: string;
  //   catalogItemId: string;
  // }) {
  //   schedule.itemId = props.itemId;
  //   schedule.productId = props.productId;
  //   schedule.productName = props.productName;
  //   schedule.catalogItemId = props.catalogItemId;
  // }

  function setScheduleType(props: { careType: string }) {
    schedule.careType = props.careType;
    return schedule;
  }

  function setPayment(payment?: Payment) {
    schedule.payment = payment;
    return schedule;
  }

  function setUnit(unit: Unit, localId: string) {
    schedule.unit = createUnitAppointment(unit);
    schedule.unitId = unit.id_sgh;
    schedule.itemId = unit.itemId;
    schedule.catalogItemId = unit.catalogItemId;
    schedule.isKit = unit.isKit;
    schedule.localId = localId;
    return schedule;
  }

  function setChannel(channel: string) {
    schedule.channel = channel;
    return schedule;
  }

  function setSlotsOptions(selectedSlotOptions: SlotHour[]) {
    schedule.selectedSlotOptions = selectedSlotOptions;
    return schedule;
  }

  function setResource(useResource: boolean, resource?: Resource) {
    schedule.resource = resource || undefined;
    schedule.useResouce = useResource;
    return schedule;
  }

  function setResourceId(resourceId: string) {
    schedule.resourceId = resourceId;
    return schedule;
  }

  function setTime(time: DisplayTimes) {
    schedule.time = time;
    return schedule;
  }

  function build() {
    return schedule;
  }

  const validateSchedule = () => {
    checkPropertyError();
    return schedule;
  };

  return {
    setPatient,
    setProduct,
    validateSchedule,
    // setProductInfo,
    setScheduleType,
    setPayment,
    setUnit,
    setSlotsOptions,
    setResource,
    build,
    setChannel,
    setTime,
    setPatientName,
    setResourceId,
    setPatientFull
  };
};
